import type { Catalogs, PlainKey, Translation } from "@amtana/i18n/catalog";
import type { Vendor } from "@amtana/managed-mailbox-contract";

// The words of the purchase wizard (provider, identity, domains, mailboxes,
// checkout), its summary and its toasts, in English and Spanish. A table of
// labels holds keys, not words; the wizard reads them through `usePurchaseCopy`.

export type PurchaseCopyKey = keyof typeof EN;
export type PurchaseCatalog = typeof EN;

const EN = {
  screenTitle: "Managed Mailboxes · {product}",
  breadcrumbHome: "Home",
  breadcrumbSenders: "Senders",
  pageTitle: "Managed Mailboxes",
  pageDescription: "Purchase domains and mailboxes for your cold email campaigns",
  viewOrders: "View Orders",
  viewDomains: "View Domains",
  purchaseSteps: "Purchase steps",
  stepVendorTitle: "Provider",
  stepVendorDescription: "Infrastructure",
  stepIdentityTitle: "Identity",
  stepIdentityDescription: "Name & signature",
  stepDomainsTitle: "Domains",
  stepDomainsDescription: "What to buy",
  stepMailboxesTitle: "Mailboxes",
  stepMailboxesDescription: "Addresses",
  stepCheckoutTitle: "Checkout",
  stepCheckoutDescription: "Payment method",
  previous: "Previous",
  next: "Next",
  unlockTitle: "Upgrade to unlock Managed Mailboxes",
  unlockDescription: "Starter Plan Required. Managed mailboxes are available on every paid plan. {features}.",
  unlockFeatureDomains: "Purchase custom domains for cold email",
  unlockFeatureMailboxes: "Create professional mailboxes",
  unlockFeatureDns: "Auto-configured DNS & security",
  unlockFeatureWarmup: "Integrated warmup process",
  upgradePlan: "Upgrade Plan",
  limitReachedTitle: "Domain Limit Reached",
  limitReachedDescription: {
    one: "You have {current} of {count} domain on your current plan. Upgrade to add more domains.",
    other: "You have {current} of {count} domains on your current plan. Upgrade to add more domains.",
  },
  upgradeForMoreDomains: "Upgrade for More Domains",
  domainsUsed: {
    one: "{current} of {count} domain used on your plan.",
    other: "{current} of {count} domains used on your plan.",
  },
  selectProviderTitle: "Select a Provider",
  selectProviderDescription:
    "Choose the mailbox type. All include your own domains and automatic setup; what changes is the price and where they are hosted.",
  recommended: "Recommended",
  vendorPrice: "{mailbox} / mailbox / month · Domain ~{domain} / year",
  vendorSmtpDescription: "Cost-effective mailboxes on private infrastructure, ready for cold email.",
  vendorSmtpFeatureServer: "Dedicated server per domain",
  vendorSmtpFeatureDns: "Auto-configured DNS (plug & play)",
  vendorGoogleDescription:
    "Professional mailboxes on Google's trusted sending infrastructure. Managed by Google.",
  vendorGoogleFeatureGmail: "Trusted Gmail sending infrastructure",
  vendorGoogleFeaturePanel: "Dedicated admin panel per domain",
  vendorFeatureSecurity: "Pre-configured security with MFA + DNS setup",
  vendorMicrosoftDescription:
    "Business mailboxes powered by Microsoft's email infrastructure. Managed by Microsoft.",
  vendorMicrosoftFeatureOutlook: "Outlook enterprise-grade infrastructure",
  vendorMicrosoftFeatureAzure: "Enterprise Azure infrastructure",
  chooseHowTitle: "1. Choose how to create it",
  chooseHowDescription: "You can edit any detail afterwards.",
  nationality: "Nationality",
  nationalityAny: "Any",
  nationalitySpanish: "Spanish",
  nationalityAmerican: "American",
  nationalityBritish: "British",
  nationalityGerman: "German",
  nationalityItalian: "Italian",
  gender: "Gender",
  genderAny: "Any",
  genderFemale: "Female",
  genderMale: "Male",
  context: "Context",
  contextPlaceholder: "e.g. works in B2B sales for a software company",
  generating: "Generating…",
  generateAnother: "Generate another",
  generateSender: "Generate sender",
  orUseYourOwn: "Or use your own details below if you will send under your real name.",
  senderDetailsTitle: "2. Sender details",
  senderDetailsDescription: "Only first and last name are required.",
  firstName: "First name",
  firstNamePlaceholder: "John",
  lastName: "Last name",
  lastNamePlaceholder: "Doe",
  jobTitle: "Job title",
  jobTitlePlaceholder: "Sales Manager",
  company: "Company",
  companyPlaceholder: "Your company",
  website: "Website",
  websitePlaceholder: "yourcompany.com",
  profilePhoto: "Profile photo",
  profilePhotoPlaceholder: "Paste an image URL (https://…)",
  signatureTitle: "3. Signature",
  signatureDescription: "Leave it empty to use the signature built from the details above.",
  signature: "Signature",
  signaturePlaceholder: "Best regards,\nJohn Doe",
  suggestionsFor: "Suggestions for {name}",
  suggestionsDescription: "The domains are named after your sender. Filter or type another name.",
  domainsPicked: "{count} of {max} max",
  filterPlaceholder: "Filter or type another name…",
  extension: "Extension",
  allExtensions: "All extensions",
  maxPrice: "Max price",
  allPrices: "All prices",
  upToPrice: "up to {price}",
  checkingAvailability: "Checking availability with the registrar…",
  domainBanned: "Domain banned",
  pricePerYear: "{price} / year",
  notAvailable: "Not available",
  added: "Added",
  add: "Add",
  showMore: "Show {count} more",
  nothingFree:
    "Nothing free under that price. Try another extension, a higher ceiling or change the sender's name.",
  mailboxesTitle: "Mailboxes",
  mailboxesDescription:
    "We prepared {count} with your name. Use them as they are, or change the part before the @.",
  mailboxesRecommended: "{count} of {max} recommended",
  removePart: "Remove {part}",
  addMailbox: "Add mailbox",
  mailboxPlaceholder: "name",
  nameAlreadyListed: "This name is already in the list",
  addMoreTitle: "Add more mailboxes?",
  addMoreDescription:
    "We recommend a maximum of 3 mailboxes per domain for optimal deliverability. Are you sure you want to add more?",
  addAnyway: "Add anyway",
  checkoutTitle: "Complete Your Purchase",
  checkoutDescription: "Review your order and choose how to pay. We take care of the domain registration.",
  sender: "Sender",
  paymentMethod: "Payment method",
  paymentMethodValue: "Visa •••• 4242 (simulated)",
  notesTitle: "Notes",
  notesDescription:
    "Mailboxes auto-renew monthly. Additional mailboxes cannot be added to an existing domain after purchase. Mailboxes are usually ready a few minutes after payment.",
  simulationNotice: "I understand this is a simulation: nothing is registered or charged.",
  processing: "Processing...",
  pay: "Pay",
  payAmount: "Pay {amount}",
  summaryTitle: "Summary",
  summaryEmpty: "Configure your order to see the price.",
  summaryProvider: "Provider",
  summaryDomains: "Domains",
  summaryDomainsValue: "{count} · {price} / year",
  summaryMailboxes: "Mailboxes",
  summaryMailboxesValue: "{count} · {price} / month",
  summaryCapacity: "Estimated capacity",
  summaryCapacityValue: "up to {count} emails/day",
  summaryDelivery: "Estimated delivery",
  summaryDeliveryValue: "~{count} min",
  summaryTotal: "Total to pay",
  nameChangedToast:
    "The sender's name changed, so the domains and addresses made from the old name were removed.",
  couldNotGenerate: "We couldn't generate an identity. You can fill in the fields yourself",
  maxDomainsReached: {
    one: "Maximum of {count} domain reached for your plan",
    other: "Maximum of {count} domains reached for your plan",
  },
  orderPlaced: "Order Placed Successfully!",
  couldNotPlaceOrder: "Could not place the order",
} as const;

const ES: Translation<PurchaseCatalog> = {
  screenTitle: "Buzones gestionados · {product}",
  breadcrumbHome: "Inicio",
  breadcrumbSenders: "Remitentes",
  pageTitle: "Buzones gestionados",
  pageDescription: "Compra dominios y buzones para tus campañas de correo en frío",
  viewOrders: "Ver pedidos",
  viewDomains: "Ver dominios",
  purchaseSteps: "Pasos de la compra",
  stepVendorTitle: "Proveedor",
  stepVendorDescription: "Infraestructura",
  stepIdentityTitle: "Identidad",
  stepIdentityDescription: "Nombre y firma",
  stepDomainsTitle: "Dominios",
  stepDomainsDescription: "Qué comprar",
  stepMailboxesTitle: "Buzones",
  stepMailboxesDescription: "Direcciones",
  stepCheckoutTitle: "Pago",
  stepCheckoutDescription: "Forma de pago",
  previous: "Anterior",
  next: "Siguiente",
  unlockTitle: "Mejora tu plan para desbloquear los buzones gestionados",
  unlockDescription:
    "Se requiere el plan Starter. Los buzones gestionados están disponibles en todos los planes de pago. {features}.",
  unlockFeatureDomains: "Compra dominios propios para correo en frío",
  unlockFeatureMailboxes: "Crea buzones profesionales",
  unlockFeatureDns: "DNS y seguridad configurados automáticamente",
  unlockFeatureWarmup: "Proceso de calentamiento integrado",
  upgradePlan: "Mejorar plan",
  limitReachedTitle: "Límite de dominios alcanzado",
  limitReachedDescription: {
    one: "Tienes {current} de {count} dominio en tu plan actual. Mejora tu plan para añadir más dominios.",
    other: "Tienes {current} de {count} dominios en tu plan actual. Mejora tu plan para añadir más dominios.",
  },
  upgradeForMoreDomains: "Mejorar para tener más dominios",
  domainsUsed: {
    one: "{current} de {count} dominio usado en tu plan.",
    other: "{current} de {count} dominios usados en tu plan.",
  },
  selectProviderTitle: "Elige un proveedor",
  selectProviderDescription:
    "Elige el tipo de buzón. Todos incluyen tus propios dominios y configuración automática; cambian el precio y dónde se alojan.",
  recommended: "Recomendado",
  vendorPrice: "{mailbox} / buzón / mes · Dominio ~{domain} / año",
  vendorSmtpDescription: "Buzones económicos en infraestructura privada, listos para correo en frío.",
  vendorSmtpFeatureServer: "Servidor dedicado por dominio",
  vendorSmtpFeatureDns: "DNS configurado automáticamente (listo para usar)",
  vendorGoogleDescription:
    "Buzones profesionales en la infraestructura de envío de confianza de Google. Gestionados por Google.",
  vendorGoogleFeatureGmail: "Infraestructura de envío de confianza de Gmail",
  vendorGoogleFeaturePanel: "Panel de administración dedicado por dominio",
  vendorFeatureSecurity: "Seguridad preconfigurada con MFA y DNS",
  vendorMicrosoftDescription:
    "Buzones empresariales sobre la infraestructura de correo de Microsoft. Gestionados por Microsoft.",
  vendorMicrosoftFeatureOutlook: "Infraestructura de nivel empresarial de Outlook",
  vendorMicrosoftFeatureAzure: "Infraestructura empresarial de Azure",
  chooseHowTitle: "1. Elige cómo crearlo",
  chooseHowDescription: "Después puedes editar cualquier dato.",
  nationality: "Nacionalidad",
  nationalityAny: "Cualquiera",
  nationalitySpanish: "Española",
  nationalityAmerican: "Estadounidense",
  nationalityBritish: "Británica",
  nationalityGerman: "Alemana",
  nationalityItalian: "Italiana",
  gender: "Género",
  genderAny: "Cualquiera",
  genderFemale: "Mujer",
  genderMale: "Hombre",
  context: "Contexto",
  contextPlaceholder: "p. ej. trabaja en ventas B2B para una empresa de software",
  generating: "Generando…",
  generateAnother: "Generar otro",
  generateSender: "Generar remitente",
  orUseYourOwn: "O usa tus propios datos abajo si vas a enviar con tu nombre real.",
  senderDetailsTitle: "2. Datos del remitente",
  senderDetailsDescription: "Solo el nombre y el apellido son obligatorios.",
  firstName: "Nombre",
  firstNamePlaceholder: "Juan",
  lastName: "Apellido",
  lastNamePlaceholder: "Pérez",
  jobTitle: "Cargo",
  jobTitlePlaceholder: "Gerente de ventas",
  company: "Empresa",
  companyPlaceholder: "Tu empresa",
  website: "Sitio web",
  websitePlaceholder: "tuempresa.com",
  profilePhoto: "Foto de perfil",
  profilePhotoPlaceholder: "Pega la URL de una imagen (https://…)",
  signatureTitle: "3. Firma",
  signatureDescription: "Déjala vacía para usar la firma creada con los datos de arriba.",
  signature: "Firma",
  signaturePlaceholder: "Saludos cordiales,\nJuan Pérez",
  suggestionsFor: "Sugerencias para {name}",
  suggestionsDescription: "Los dominios llevan el nombre de tu remitente. Filtra o escribe otro nombre.",
  domainsPicked: "{count} de {max} máx.",
  filterPlaceholder: "Filtra o escribe otro nombre…",
  extension: "Extensión",
  allExtensions: "Todas las extensiones",
  maxPrice: "Precio máximo",
  allPrices: "Todos los precios",
  upToPrice: "hasta {price}",
  checkingAvailability: "Comprobando la disponibilidad con el registrador…",
  domainBanned: "Dominio bloqueado",
  pricePerYear: "{price} / año",
  notAvailable: "No disponible",
  added: "Añadido",
  add: "Añadir",
  showMore: "Mostrar {count} más",
  nothingFree:
    "No hay nada libre por debajo de ese precio. Prueba otra extensión, un precio más alto o cambia el nombre del remitente.",
  mailboxesTitle: "Buzones",
  mailboxesDescription: "Preparamos {count} con tu nombre. Úsalos tal cual o cambia la parte antes de la @.",
  mailboxesRecommended: "{count} de {max} recomendados",
  removePart: "Quitar {part}",
  addMailbox: "Añadir buzón",
  mailboxPlaceholder: "nombre",
  nameAlreadyListed: "Este nombre ya está en la lista",
  addMoreTitle: "¿Añadir más buzones?",
  addMoreDescription:
    "Recomendamos un máximo de 3 buzones por dominio para una mejor entregabilidad. ¿Seguro que quieres añadir más?",
  addAnyway: "Añadir de todos modos",
  checkoutTitle: "Completa tu compra",
  checkoutDescription:
    "Revisa tu pedido y elige cómo pagar. Nosotros nos encargamos del registro del dominio.",
  sender: "Remitente",
  paymentMethod: "Forma de pago",
  paymentMethodValue: "Visa •••• 4242 (simulada)",
  notesTitle: "Notas",
  notesDescription:
    "Los buzones se renuevan cada mes. No se pueden añadir más buzones a un dominio ya comprado. Los buzones suelen estar listos unos minutos después del pago.",
  simulationNotice: "Entiendo que esto es una simulación: no se registra ni se cobra nada.",
  processing: "Procesando...",
  pay: "Pagar",
  payAmount: "Pagar {amount}",
  summaryTitle: "Resumen",
  summaryEmpty: "Configura tu pedido para ver el precio.",
  summaryProvider: "Proveedor",
  summaryDomains: "Dominios",
  summaryDomainsValue: "{count} · {price} / año",
  summaryMailboxes: "Buzones",
  summaryMailboxesValue: "{count} · {price} / mes",
  summaryCapacity: "Capacidad estimada",
  summaryCapacityValue: "hasta {count} correos/día",
  summaryDelivery: "Entrega estimada",
  summaryDeliveryValue: "~{count} min",
  summaryTotal: "Total a pagar",
  nameChangedToast:
    "El nombre del remitente cambió, así que se quitaron los dominios y las direcciones creados con el nombre anterior.",
  couldNotGenerate: "No pudimos generar una identidad. Puedes rellenar los campos tú mismo",
  maxDomainsReached: {
    one: "Máximo de {count} dominio alcanzado para tu plan",
    other: "Máximo de {count} dominios alcanzado para tu plan",
  },
  orderPlaced: "¡Pedido realizado con éxito!",
  couldNotPlaceOrder: "No se pudo hacer el pedido",
};

export const MANAGED_MAILBOX_PURCHASE_CATALOGS: Catalogs<PurchaseCatalog> = { en: EN, es: ES };

type Label = PlainKey<PurchaseCatalog>;

/** What each provider says about itself: its description and its feature chips, as keys. */
export const VENDOR_COPY: Readonly<Record<Vendor, { description: Label; features: readonly Label[] }>> = {
  smtp: {
    description: "vendorSmtpDescription",
    features: ["vendorSmtpFeatureServer", "vendorSmtpFeatureDns"],
  },
  google: {
    description: "vendorGoogleDescription",
    features: ["vendorGoogleFeatureGmail", "vendorGoogleFeaturePanel", "vendorFeatureSecurity"],
  },
  microsoft: {
    description: "vendorMicrosoftDescription",
    features: ["vendorMicrosoftFeatureOutlook", "vendorMicrosoftFeatureAzure", "vendorFeatureSecurity"],
  },
};

export const UNLOCK_FEATURES: readonly Label[] = [
  "unlockFeatureDomains",
  "unlockFeatureMailboxes",
  "unlockFeatureDns",
  "unlockFeatureWarmup",
];

/** The nationality is sent to the server as this English word; only the label is translated. */
export const NATIONALITIES = [
  { value: "Spanish", label: "nationalitySpanish" },
  { value: "American", label: "nationalityAmerican" },
  { value: "British", label: "nationalityBritish" },
  { value: "German", label: "nationalityGerman" },
  { value: "Italian", label: "nationalityItalian" },
] as const satisfies ReadonlyArray<{ value: string; label: Label }>;

export const GENDERS = [
  { value: "female", label: "genderFemale" },
  { value: "male", label: "genderMale" },
] as const satisfies ReadonlyArray<{ value: string; label: Label }>;
